<template>
  <div class="player-container">
    <template v-if="!isCorrectData">
      <div class="row">
        <p><strong>Произошла ошибка. На этой камере невозможно запросить видеопоток.</strong></p>
      </div>
    </template>

    <template v-else-if="hasAnyToken">
      <div class="player-dialog">
        <div :class="{'player-dialog__content': analyticLinesConfig || showSchedule}">
          <div class="player-dialog__player">
            <SmartPlayer
              ref="smartPlayer"
              :analytic-zones-config="analyticZonesConfig"
              :analytic-lines-config="analyticLinesConfig"
              :analytic-parking-stops-config="analyticParkingStopsConfig"
              :vendor-name="vendorName"
              :archive-token="archiveToken"
              :available-archive-fragments="availableArchiveFragments"
              :camera-number="cameraNumber"
              :domain="domain"
              :get-line-fragments="getLineFragments"
              :has-sound="hasSound"
              :http-protocol="$store.getters.protocolVideoOverHTTP"
              :initial-low-latency-mode="lowLatencyMode"
              :initial-time-shift="initialTimeShift"
              :live-token="liveToken"
              :max-unix-download-delta="maxUnixDownloadDelta"
              :min-unix-download-delta="minUnixDownloadDelta"
              :possible-low-latency-hls="false"
              :stream-count="streamCount"
              :ws-protocol="$store.getters.protocolVideoOverWS"
              @download-video="downloadVideo"
              @save-zones="saveZones"
              @save-lines="saveLines"
              @save-parking-stops="saveParkingSpots"
            />

            <!--iframe для скачивания no-referrer -->
            <iframe
              :src="downloadUrl"
              frameborder="0"
              height="1"
              referrerpolicy="no-referrer"
              width="1"
            />
          </div>
          <div v-if="analyticLinesConfig" class="analytics-description">
            <h3 class="analytics-title">
              Настройка подсчёта посетителей
            </h3>
            <p><strong class="highlight-green">Зелёная точка</strong> — это точка входа. Система использует её для определения направления движения.</p>
            <p><strong class="highlight-red">Красная линия</strong> — это контрольная линия, по которой происходит сам подсчёт.</p>

            <div class="analytics-important">
              <h4>Важно:</h4>
              <ul>
                <li>Зелёная точка должна находиться с одной из сторон контрольной линии, но <strong>не должна располагаться прямо на линии</strong>.</li>
                <li>Точка входа задаёт, с какой стороны система будет считать, что человек входит.</li>
                <li>Если человек двигается <strong>от зелёной точки через линию</strong> — засчитывается <strong>вход</strong>.</li>
                <li>Если в обратную сторону — засчитывается <strong>выход</strong>.</li>
              </ul>
            </div>
          </div>
          <div>
            <ScheduleList
              v-if="showSchedule"
              :camera-number="cameraNumber"
              analytic-type="perimeter_security"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  ACTION_LOAD_RECORDING_STATUSES,
  ACTION_GET_DOWNLOAD_TOKEN,
} from "@/store/cameras/index.js";
import {methodsForDialogMixin} from "@/components/pacs/mixins.js";
import {
  AnalyticZonesConfig,
  AnalyticLinesConfig,
  AnalyticParkingStopsConfig
} from "camsng-frontend-shared/components/smartPlayer/mixins.js";
import SmartPlayer from "camsng-frontend-shared/components/smartPlayer/SmartPlayer.vue";
import {MAX_UNIX_DOWNLOAD_DELTA_FOR_PLAYER, MIN_UNIX_DOWNLOAD_DELTA_FOR_PLAYER} from "@/utils/consts.js";
import ScheduleList from "@/components/pacs/analytics/schedule/ScheduleList.vue";

/**
 * Компонент диалога для запроса токена для получения видео с конкретной камеры и сохранения зоны.
 */
export default {
  components: {
    SmartPlayer,
    ScheduleList
  },
  mixins: [methodsForDialogMixin],
  props: {
    /**
     * Номер камеры для получения токена.
     */
    cameraNumber: {
      type: String,
      default: null,
    },
    /**
     * Заголовок камеры.
     */
    title: {
      type: String,
      default: "",
    },
    /**
     * Токен доступа к архиву.
     */
    archiveToken: {
      type: String,
      default: "",
    },
    /**
     * Токен прямой трансляции.
     */
    liveToken: {
      type: String,
      default: "",
    },
    /**
     * Токен для скачивания.
     */
    downloadToken: {
      type: String,
      default: "",
    },
    /**
     * Домен сервера с которого можно получить поток с камеры.
     */
    domain: {
      type: String,
      default: null,
    },
    /**
     * Вендор.
     */
    vendorName: {
      type: String,
      default: null,
    },
    /**
     * Количество доступных потоков.
     */
    streamCount: {
      type: Number,
      default: null,
    },
    /**
     * Флаг, определяющий возможность настройки громкости.
     */
    hasSound: {
      type: Boolean,
      default: false,
    },
    /**
     * Флаг, определяющий отображение расписания.
     */
    showSchedule: {
      type: Boolean,
      default: false,
    },
    /**
     * Набор настроек и прочей информации по зонам аналитики.
     */
    analyticZonesConfig: {
      type: AnalyticZonesConfig,
      default: null,
    },
    /**
     * Набор настроек и прочей информации по подсчету посетителей.
     */
    analyticLinesConfig: {
      type: AnalyticLinesConfig,
      default: null,
    },
    /**
     * Набор настроек и прочей информации по парковкам.
     */
    analyticParkingStopsConfig: {
      type: AnalyticParkingStopsConfig,
      default: null,
    },
  },
  data() {
    const isCorrectData = this.cameraNumber && this.domain && this.streamCount,
          initialTimeShift = new Date();
    initialTimeShift.setMinutes(initialTimeShift.getMinutes() - 10, 0, 0);

    return {
      isCorrectData: isCorrectData,
      isLoading: true, // Стартуем с true, чтобы сразу показать спиннер
      textError: "",
      lowLatencyMode: false,
      timerIdForReceivingRecordingStatus: null,
      availableArchiveFragments: [],
      initialTimeShift: null,
      isLoadingDownloadToken: false,
      downloadUrl: "",
      minUnixDownloadDelta: MIN_UNIX_DOWNLOAD_DELTA_FOR_PLAYER,
      maxUnixDownloadDelta: MAX_UNIX_DOWNLOAD_DELTA_FOR_PLAYER,
    };
  },
  computed: {
    /**
     * @return {Boolean} Вернет true если есть хоть какой-то токен.
     */
    hasAnyToken() {
      return Boolean(this.archiveToken || this.liveToken);
    }
  },
  mounted() {
    this.requestTokens();
  },
  beforeDestroy() {
    clearInterval(this.timerIdForReceivingRecordingStatus);
  },
  methods: {
    /**
     * Отправка запроса.
     * В случае успешного получения токена открывается плеер для воспроизведения видео.
     */
    async requestTokens() {
      this.isLoading = true;
      this.textError = "";
      try {
        await this.receiveRecordingStatus();
        this.timerIdForReceivingRecordingStatus = setInterval(this.receiveRecordingStatus, 5000);
      } catch (error) {
        console.error('Error loading recording status:', error);
        this.textError = error.message;
      } finally {
        setTimeout(() => {
          this.isLoading = false;
        }, 300);
      }
    },
    /**
     * Получение доступных фрагментов видео от сервера.
     */
    async receiveRecordingStatus() {
      this.availableArchiveFragments = await this.$store.dispatch(`cameras/${ACTION_LOAD_RECORDING_STATUSES}`, {
        cameraNumber: this.cameraNumber,
        domain: this.domain,
        token: this.archiveToken,
      });
    },
    /**
     * Функция должна вернуть актуальный набор данных для отображения на временной шкале.
     * Для доступного архива фильтруются доступные участки для рисования, уточняется их цвет и низший приоритет.
     *
     * @param {Date} leftBoundary
     * @param {Date} rightBoundary
     * @return {Promise<Array<Array<Date, Date, String, Number>>>}
     */
    async getLineFragments(leftBoundary, rightBoundary) {
      return this.availableArchiveFragments.filter(([dateStart, dateEnd]) => {
        return +leftBoundary < +dateEnd && +rightBoundary > +dateStart;
      }).map(([dateStart, dateEnd]) => {
        return [dateStart, dateEnd, "#bbbbbb", 0];
      });
    },
    /**
     * Отправка запроса на получение токена для скачивания фрагмента видео.
     * При его получении формирование URL и автоматический запуск скачивания.
     *
     * @param {Function} getterDownloadUrl
     * @param {Number} unixDownloadFrom
     * @param {Number} unixDownloadDelta
     */
    async downloadVideo([getterDownloadUrl, unixDownloadFrom, unixDownloadDelta]) {
      // Конкретные ограничения по длительности запрашиваемого видео.
      if ((unixDownloadDelta < MIN_UNIX_DOWNLOAD_DELTA_FOR_PLAYER) || (unixDownloadDelta > MAX_UNIX_DOWNLOAD_DELTA_FOR_PLAYER)) {
        this.$camsdals.alert(this.$t('invalidDownloadRange'));
        return;
      }
      let tokenDownload = null;
      this.isLoadingDownloadToken = true;
      try {
        const token = await this.$store.dispatch(`cameras/${ACTION_GET_DOWNLOAD_TOKEN}`, {
          number: this.cameraNumber,
          tokenDownloadDuration: unixDownloadDelta,
          tokenDownloadStart: unixDownloadFrom,
        });
        tokenDownload = token;
      } catch (error) {
        devLog("[downloadVideo]", error);
      }
      if (tokenDownload) {
        this.downloadUrl = getterDownloadUrl(tokenDownload);
      } else {
        this.$camsdals.alert(this.$t('downloadError'));
      }
      this.isLoadingDownloadToken = false;
    },
    /**
     * Передаст параметры зон из плеера для сохранения в родительский компонент.
     *
     * @param {AnalyticZonesConfig} analyticZonesConfig
     */
    saveZones(analyticZonesConfig) {
      this.closeDialog(analyticZonesConfig);
      this.$notify({
        group: "main",
        text: this.$t('saveSuccess'),
        duration: 5000,
        type: "success"
      });
    },
    saveParkingSpots(analyticParkingStopsConfig) {
      this.closeDialog(analyticParkingStopsConfig);
      this.$notify({
        group: "main",
        text: this.$t('saveSuccess'),
        duration: 5000,
        type: "success"
      });
    },
    saveLines(analyticLinesConfig) {
      this.closeDialog(analyticLinesConfig);
      this.$notify({
        group: "main",
        text: this.$t('saveSuccess'),
        duration: 5000,
        type: "success"
      });
    }
  }
};
</script>

<style scoped>
.player-container {
  position: relative;
  min-height: 400px;
  width: 100%;
  height: 100%;
}

.player-dialog {
  width: 100%;
  box-sizing: border-box;
  position: relative;
  min-height: 400px;
}
.player-dialog__content {
  display: grid;
  grid-template-columns: minmax(810px, 1fr) minmax(350px, 1fr);
  gap: 20px;
  align-items: flex-start;
}
.player-dialog__player {
  min-width: 0;
  position: relative;
}
.player-dialog__player ::v-deep .smart-player__display {
  height: 460px;
}

.player-dialog__player ::v-deep .smart-player:fullscreen,
.player-dialog__player ::v-deep .smart-player:-webkit-full-screen {
  width: 100vw;
  height: 100vh;
}

.player-dialog__player ::v-deep .smart-player:fullscreen .smart-player__display,
.player-dialog__player ::v-deep .smart-player:-webkit-full-screen .smart-player__display {
  height: 100%;
}

.player-dialog__player ::v-deep .smart-player:fullscreen .smart-player__timeline,
.player-dialog__player ::v-deep .smart-player:fullscreen .smart-player__controls,
.player-dialog__player ::v-deep .smart-player:-webkit-full-screen .smart-player__timeline,
.player-dialog__player ::v-deep .smart-player:-webkit-full-screen .smart-player__controls {
  width: 100%;
}

@media (max-width: 1200px) {
  .player-dialog__player ::v-deep .smart-player__display {
  }
}
.player-dialog__description {
  min-width: 0;
}
@media (max-width: 1200px) {
  .player-dialog__content {
    grid-template-columns: 1fr;
  }
  .player-dialog__description {
    margin-top: 12px;
  }
}

.analytics-description {
  background-color: #f9f9f9;
  padding: 20px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.analytics-title {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
}

.highlight-green {
  color: #27ae60;
  font-weight: 600;
}

.highlight-red {
  color: #e74c3c;
  font-weight: 600;
}

.analytics-important {
  margin-top: 16px;
  padding: 12px;
  border-left: 4px solid #f39c12;
  background-color: #fff8e1;
  border-radius: 6px;
}

.analytics-important h4 {
  margin: 0 0 8px;
  font-size: 15px;
  color: #d35400;
  font-weight: 600;
}

.analytics-important ul {
  padding-left: 18px;
  margin: 0;
}

.analytics-important li {
  margin-bottom: 6px;
}


</style>
